import { Container, Graphics, Sprite } from 'pixi.js';
import { WEAPONS, type ProjectileBehavior } from '@/config/weapons';
import {
  WEAPON_CATEGORY_COLOR,
  WEAPON_RARITY_COLOR,
  weaponGlyphKey,
} from '@/config/weapon-visuals';
import { COLORS } from '@/config/colors';
import { getTexture } from '@/assets/resolver';
import { weaponDps } from '@/entities/weapon-dps';
import { t, type I18nKey } from '@/i18n';
import { cardDescription, cardName, type CardDef } from '@/upgrades/cards';
import { RARITY_COLOR, cardTint, drawCardIcon } from './card-icons';
import type { FocusItem, ShopState } from './shop-types';
import { bodyText, chamferPanel, hexPath, label } from './widgets';
import { weaponName, weaponRarityName } from './weapon-labels';

/** Collection chip — owned card icon (stat-family tint) with a ×count badge. */
export function buildCollectionChip(card: CardDef, count: number, selected = false): Container {
  const c = new Container();
  const rarity = RARITY_COLOR[card.rarity];
  const tint = cardTint(card);
  const g = new Graphics();
  hexPath(g, 0, 0, 24).fill(0x0f0c1a)
    .stroke({ color: selected ? COLORS.uiAccent : rarity, width: selected ? 3.5 : 2.5 });
  c.addChild(g);
  const icon = drawCardIcon(new Graphics(), card.icon, tint);
  icon.scale.set(0.62);
  c.addChild(icon);
  if (count > 1) {
    const badge = label(`×${count}`, 12, COLORS.playerYellow, 0);
    badge.anchor.set(0.5);
    badge.position.set(16, 18);
    c.addChild(badge);
  }
  return c;
}

/** Hover/focus tooltip for an owned card — name, level, description. */
export function buildCardTooltip(card: CardDef, count: number): Container {
  const c = new Container();
  const w = 250;
  const desc = bodyText(cardDescription(card), 13, 0xc4c9d4, w - 28);
  desc.style.align = 'left';
  const h = 60 + desc.height;
  const g = new Graphics();
  const tint = cardTint(card);
  chamferPanel(g, w, h, { fill: 0x0f0c1a, fillAlpha: 0.97, stroke: tint, notch: 10 });
  c.addChild(g);
  const name = label(cardName(card), 14, tint, 1);
  name.position.set(14, 10);
  const meta = label(
    `${t('shop.level', { n: count })} · ${t(`cardrarity.${card.rarity}` as I18nKey)}`,
    12, RARITY_COLOR[card.rarity], 2,
  );
  meta.position.set(14, 30);
  desc.anchor.set(0, 0);
  desc.position.set(14, 50);
  c.addChild(name, meta, desc);
  return c;
}



/** Positioned hover/focus popup for the selected chip or weapon, if any. */
export function buildFocusTooltip(
  s: ShopState,
  sel: FocusItem | undefined,
  chipPos: (i: number) => [number, number],
  layout: { designW: number; maxChips: number },
): Container | null {
  if (sel?.kind === 'chip') {
    const idx = s.collection.findIndex((e) => e.card.id === sel.cardId);
    const entry = s.collection[idx];
    if (!entry || idx >= layout.maxChips) return null;
    const tip = buildCardTooltip(entry.card, entry.count);
    const [cx, cy] = chipPos(idx);
    tip.position.set(
      Math.min(Math.max(20, cx - tip.width / 2), layout.designW - tip.width - 20),
      cy + 34,
    );
    return tip;
  }
  // Weapons: no tooltip — their stats live inline on the socket rows (H2)
  return null;
}

const TYPE_KEY: Record<ProjectileBehavior, I18nKey> = {
  straight: 'wstat.type.direct',
  decel: 'wstat.type.lobbed',
  homing: 'wstat.type.homing',
};

function fmt(n: number): string {
  return n < 10 ? n.toFixed(1) : String(Math.round(n));
}

/**
 * Weapon info panel (docs/12 M1) — opened with Y/I on a focused hangar
 * weapon. Numbers are the LIVE build (docs/09 W3): cards, average crit,
 * mount tier and rack penalty folded in; area damage on its own line.
 */
export function buildWeaponInfo(weaponId: string, s: ShopState): Container {
  const c = new Container();
  const def = WEAPONS[weaponId];
  const holder = s.ctx.weapon;
  const dps = weaponDps(
    weaponId,
    { gunTier: holder.gunTier, capacity: s.equipCapacity, equipped: s.equipped },
    s.ctx.stats,
  );
  if (!def || !dps) return c;
  const W = 320;
  const p = def.projectile;
  const typeName = t(TYPE_KEY[p.behavior]);
  const rows: Array<[string, string]> = [
    [t('wstat.behavior'), dps.aoeRadius > 0 ? t('wstat.type.aoe', { type: typeName }) : typeName],
    [t('wstat.perShot'), dps.mounts > 1 ? `${fmt(dps.perShot)} ×${dps.mounts}` : fmt(dps.perShot)],
    [t('wstat.rate'), `${dps.rate.toFixed(1)}/s`],
    [t('wstat.dps'), String(Math.round(dps.dps))],
  ];
  if (dps.aoeRadius > 0) {
    rows.push([
      t('wstat.areaDps'),
      t('wstat.areaValue', { dps: Math.round(dps.aoeDps), r: dps.aoeRadius }),
    ]);
  }
  const ROW_Y = 52;
  const ROW_H = 24;
  // Footnote wraps at 12 px instead of shrinking below it (docs/03 W4)
  const note = bodyText(
    t(dps.equipped ? 'wstat.noteLive' : 'wstat.noteIf', {
      m: dps.mounts, r: dps.rackMul.toFixed(2),
    }),
    12, 0x8a9099, W - 30,
  );
  note.style.align = 'left';
  const H = ROW_Y + rows.length * ROW_H + note.height + 12;
  const rarity = WEAPON_RARITY_COLOR[def.rarity];
  const accent = WEAPON_CATEGORY_COLOR[def.category];

  const g = new Graphics();
  chamferPanel(g, W, H, { fill: 0x110e1b, stroke: rarity, strokeWidth: 2, notch: 12 });
  c.addChild(g);

  const glyph = new Sprite(getTexture(weaponGlyphKey(def.id)));
  glyph.anchor.set(0.5);
  glyph.scale.set(0.46);
  glyph.position.set(30, 27);
  const name = label(weaponName(def.id), 16, 0xffffff, 1);
  name.position.set(52, 12);
  if (name.width > W - 150) name.scale.set((W - 150) / name.width);
  const rar = label(weaponRarityName(def.id), 12, rarity, 2);
  rar.anchor.set(1, 0);
  rar.position.set(W - 14, 10);
  const cat = label(def.category.toUpperCase(), 12, accent, 2);
  cat.anchor.set(1, 0);
  cat.position.set(W - 14, 28);
  c.addChild(glyph, name, rar, cat);

  rows.forEach(([k, v], i) => {
    const y = ROW_Y + i * ROW_H;
    const keyLabel = label(k, 12, 0x9aa0a6, 1);
    keyLabel.position.set(16, y);
    const valLabel = label(v, 13, i === 3 ? accent : 0xe8eaf0, 1);
    valLabel.anchor.set(1, 0);
    valLabel.position.set(W - 14, y);
    c.addChild(keyLabel, valLabel);
  });

  note.position.set(16, ROW_Y + rows.length * ROW_H + 2);
  c.addChild(note);
  return c;
}
